import { cloneDeep, compact, find, map, reduce, set } from 'es-toolkit/compat';
import { useMemo } from 'react';

import { CalculateFeaturesJob, JobNotification, JobType, NucleaiExecutorJob } from 'interfaces/job';
import { ChangeType, PayloadTransforms } from 'interfaces/notificationService';
import {
  useNotificationQueriesByArn,
  useNotificationQueriesById,
} from 'utils/queryHooks/useNotificationServicesQueries';
import { CasesParams } from 'utils/useCasesParams/types';

export const applyPayloadTransformsToNotification = ({
  casesParams,
  notification,
  payloadTransforms,
}: {
  casesParams: CasesParams;
  notification: JobNotification;
  payloadTransforms: PayloadTransforms;
}): JobNotification =>
  reduce(
    payloadTransforms,
    (currentNotification, payloadChange, payloadKey) => {
      const changeType = payloadChange?.changeType;
      if (changeType === ChangeType.add) {
        // Add the payload key to the notification payload
        if (payloadChange?.valueSource === 'casesParams') {
          set(currentNotification.payload, payloadKey, casesParams);
        } else if (payloadChange?.valueSource === 'presetValue') {
          set(currentNotification.payload, payloadKey, payloadChange?.presetValue);
        } else {
          console.warn(`Unsupported payloadChange for payload key: ${payloadKey}`, payloadChange);
        }
      } else {
        console.warn(`Unsupported payloadChange for payload key: ${payloadKey}`, payloadChange);
      }
      return currentNotification;
    },
    cloneDeep(notification)
  );

export const useSecondaryAnalysisNotifications = ({
  wholeSlideAnalysisJob,
  slideId,
  studyId,
  debug = false,
}: {
  wholeSlideAnalysisJob?: Pick<CalculateFeaturesJob | NucleaiExecutorJob, 'type' | 'params'>;
  slideId: string;
  studyId: string;
  debug?: boolean;
}) => {
  const wholeSlideAnalysisJobNotifications =
    wholeSlideAnalysisJob?.type === JobType.PostProcessingAndCalculateFeatures
      ? undefined // Currently, we only support notifications for NucleaiExecutor jobs
      : (wholeSlideAnalysisJob?.params?.graph_params?.[JobType.PostProcessingAndCalculateFeatures]
          ?.notifications as JobNotification[]);

  const notificationIds = compact(map(wholeSlideAnalysisJobNotifications, 'id'));
  const notificationArns = compact(map(wholeSlideAnalysisJobNotifications, 'arn'));

  const notificationQueriesById = useNotificationQueriesById(notificationIds, debug);
  const notificationQueriesByArn = useNotificationQueriesByArn(notificationArns, debug);

  const secondaryAnalysisNotifications = useMemo(() => {
    const casesParams: CasesParams = { studyId, slideIdsToInclude: [slideId] };
    return map(compact(wholeSlideAnalysisJobNotifications), (notification) => {
      const notificationEntry =
        // Match by ID
        find(notificationQueriesById.data, { id: notification.id }) ||
        // Match by ARN if ID is not found
        find(notificationQueriesByArn.data, { arn: notification.arn });

      // Use the notification entry metadata to add required fields
      const payloadTransforms =
        notificationEntry?.metadata?.secondaryAnalysisPayloadTransforms?.payloadTransforms || {};
      return applyPayloadTransformsToNotification({ casesParams, notification, payloadTransforms });
    });
  }, [
    studyId,
    slideId,
    wholeSlideAnalysisJobNotifications,
    notificationQueriesById.data,
    notificationQueriesByArn.data,
    wholeSlideAnalysisJob?.type,
  ]);
  return secondaryAnalysisNotifications;
};
